/**
 * Compute the flat list of Forge dbIds for a set of verified tags.
 *
 * Primary path: each tag carries a pre-resolved `.children[]` array of
 * ForgeObjects (backend `getBaseTagsByIds` populates this). We flatten
 * child.dbId across all tags, dedup, and return.
 *
 * Defensive fallback: if a tag's `.children[]` is empty but the tag itself
 * has a `.dbId`, we walk its Forge subtree via
 * `instanceTree.enumNodeChildren(tagDbId, cb, true)` — this requires
 * viewer.model to be passed, which the caller supplies only when the
 * primary path is incomplete.
 *
 * Pure function. Does NOT mutate input tags.
 *
 * @param {Array<{ dbId?: number, children?: Array<{ dbId: number }> }>} tags
 *   Tags from `/api/tags/getBaseTagsByIds`.
 * @param {object} [options]
 * @param {object} [options.model]  Optional Forge model (for subtree fallback).
 *   When supplied and a tag has empty `.children[]`, we walk the instanceTree
 *   from the tag's own dbId.
 * @returns {number[]}  Deduplicated, sorted dbIds.
 */
export function computeTagDbIds(tags, options = {}) {
  if (!Array.isArray(tags) || tags.length === 0) return [];
  const model = options.model || null;
  const instanceTree = model?.getInstanceTree?.() || null;
  const set = new Set();

  for (const tag of tags) {
    if (!tag) continue;
    const children = Array.isArray(tag.children) ? tag.children : [];
    if (children.length > 0) {
      for (const child of children) {
        if (child && Number.isFinite(child.dbId)) set.add(child.dbId);
      }
      continue;
    }
    // Fallback: empty children — use tag's own dbId + walk subtree if instanceTree available.
    if (Number.isFinite(tag.dbId)) {
      set.add(tag.dbId);
      if (instanceTree) {
        instanceTree.enumNodeChildren(
          tag.dbId,
          (childDbId) => {
            if (Number.isFinite(childDbId)) set.add(childDbId);
          },
          true,
        );
      }
    }
  }

  return Array.from(set).sort((a, b) => a - b);
}

export default computeTagDbIds;
